import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  DestinationsResponse,
  DestinationType,
  UpsertDestinationPayload,
  TestDestinationPayload,
  TestDestinationResult,
} from 'src/types/TAnalyticsDestinations';
import {
  upsertDestination,
  setDestinationEnabled,
  deleteDestination,
  testDestination,
} from '../functions/analyticsDestinationsApi';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { queryNames } from '../utils/queryNames';

export function useUpsertDestinationMutation(
  mutationOptions?: UseMutationOptions<DestinationsResponse, FetchError, UpsertDestinationPayload>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((payload: UpsertDestinationPayload) =>
      upsertDestination(payload, accessToken)),
    [accessToken],
  );
  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.analyticsDestinations]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useSetDestinationEnabledMutation(
  mutationOptions?: UseMutationOptions<
    DestinationsResponse,
    FetchError,
    { destinationType: DestinationType; enabled: boolean }
  >,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(({ destinationType, enabled }: { destinationType: DestinationType; enabled: boolean }) =>
      setDestinationEnabled(destinationType, enabled, accessToken)),
    [accessToken],
  );
  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.analyticsDestinations]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useDeleteDestinationMutation(
  mutationOptions?: UseMutationOptions<DestinationsResponse, FetchError, DestinationType>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((destinationType: DestinationType) =>
      deleteDestination(destinationType, accessToken)),
    [accessToken],
  );
  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await queryClient.invalidateQueries([queryNames.analyticsDestinations]);
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useTestDestinationMutation(
  mutationOptions?: UseMutationOptions<TestDestinationResult, FetchError, TestDestinationPayload>,
) {
  const accessToken = useAccessToken();
  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper((payload: TestDestinationPayload) =>
      testDestination(payload, accessToken)),
    [accessToken],
  );
  return useMutation(mutationFn, mutationOptions);
}
